<template>
  <section class="form-list">
    <el-form
      ref="form"
      :rules="rules"
      :model="postData"
      label-position="right"
      label-width="160px"
      class="form"
    >
      <el-form-item label="代金券类型名称" prop="voucher_type_name">
        <el-input
          v-model="postData.voucher_type_name"
          maxlength="16"
          show-word-limit
          clearable
          placeholder="请输入代金券类型名称"
        />
      </el-form-item>

      <el-form-item label="过期方式" prop="expire_mode">
        <el-radio-group v-model="postData.expire_mode">
          <el-radio :label="0">按有效时长倒计时</el-radio>
          <el-radio :label="1">指定时间过期</el-radio>
        </el-radio-group>
        <el-alert
          title="选择学员获得代金券后的过期计算方式"
          type="info"
          show-icon
          :closable="false"
        />
      </el-form-item>

      <el-form-item v-if="postData.expire_mode === 0" label="有效时长" prop="duration_days" required>
        <div class="flex-align-center">
          <v-input-number
            class="duration-input"
            placeholder="天"
            suffix-name="天"
            :is-show-suffix="true"
            :is-small-suffix="true"
            v-model="postData.duration_days"
            :min="0"
            :max="1089"
          />
          <v-input-number
            class="duration-input"
            placeholder="小时"
            suffix-name="小时"
            :is-show-suffix="true"
            :is-small-suffix="true"
            v-model="postData.duration_hours"
            :min="0"
            :max="postData.duration_days >= 1089 ? 0 : 23"
            :disabled="postData.duration_days >= 1089"
            @change="() => $refs.form.validateField('duration_days')"
          />
          <v-input-number
            class="duration-input"
            placeholder="分钟"
            suffix-name="分钟"
            :is-show-suffix="true"
            :is-small-suffix="true"
            v-model="postData.duration_minutes"
            :min="0"
            :max="postData.duration_days >= 1089 ? 0 : 59"
            :disabled="postData.duration_days >= 1089"
            @change="() => $refs.form.validateField('duration_days')"
          />
        </div>
        <el-alert
          title="学员获得代金券后的有效时长"
          type="info"
          show-icon
          :closable="false"
        />
      </el-form-item>

      <el-form-item v-else label="指定过期时间" prop="fixed_expire_time">
        <el-date-picker
          class="w100"
          v-model="postData.fixed_expire_time"
          type="datetime"
          value-format="timestamp"
          :picker-options="fixedExpireTimePickerOptions"
          placeholder="请选择指定过期时间"
        />
        <el-alert
          title="所有通过该类型发放的代金券，均在此固定时间点过期"
          type="info"
          show-icon
          :closable="false"
        />
      </el-form-item>

      <el-form-item label="发放方式" prop="issue_type">
        <v-select-voucher-issue-type
          class="w100"
          v-model="postData.issue_type"
          placeholder="请选择发放方式"
        />
        <el-alert
          title="选择代金券的发放方式"
          type="info"
          show-icon
          :closable="false"
        />
      </el-form-item>

      <el-form-item v-if="postData.issue_type === 4" label="课次券类型" prop="card_ticket_type_ids">
        <v-select-card-ticket-type
          class="w100"
          v-model="postData.card_ticket_type_ids"
          :multiple="true"
          :is-only-show-app="true"
          placeholder="请选择课次券类型"
        />
        <el-alert
          title="学员在小程序购买并支付所选课次券后自动发放该代金券，活动时间用于限定统计范围"
          type="info"
          show-icon
          :closable="false"
        />
      </el-form-item>

      <template v-if="[1, 4, 5].includes(postData.issue_type)">
        <el-form-item label="活动开始时间" prop="start_time">
          <el-date-picker
            class="w100"
            v-model="postData.start_time"
            type="datetime"
            value-format="timestamp"
            :picker-options="startTimePickerOptions"
            placeholder="请选择活动开始时间"
          />
        </el-form-item>

        <el-form-item label="活动结束时间" prop="end_time">
          <el-date-picker
            class="w100"
            v-model="postData.end_time"
            type="datetime"
            value-format="timestamp"
            :picker-options="endTimePickerOptions"
            placeholder="请选择活动结束时间"
            @change="onEndTimeChange"
          />
        </el-form-item>
      </template>

      <el-form-item label="代金券面额" prop="amount_in_cents">
        <v-input-money
          class="w100"
          placeholder="请输入代金券面额"
          :is-show-suffix="true"
          v-model="postData.amount_in_cents"
          :min="0.01"
          :max="999.99"
        />
        <el-alert
          title="代金券面额，上限 999.99 元"
          type="info"
          show-icon
          :closable="false"
        />
      </el-form-item>

      <el-form-item v-if="postData.issue_type === 5" label="小程序售价" prop="sale_price_in_cents">
        <v-input-money
          class="w100"
          placeholder="请输入小程序售价"
          :is-show-suffix="true"
          v-model="postData.sale_price_in_cents"
          :min="0.01"
          :max="999.99"
        />
        <el-alert
          title="学员在小程序实际支付的金额，必须低于代金券面额；例如面额 111 元、售价 1 元即「1 元膨胀 111 元」"
          type="info"
          show-icon
          :closable="false"
        />
      </el-form-item>

      <el-form-item v-if="postData.issue_type === 5" label="购买范围">
        <el-select
          class="w100"
          v-model="postData.purchase_scope"
          placeholder="不限制（默认）"
          clearable
        >
          <el-option
            v-for="scope in purchaseScopeOptions"
            :key="scope.value"
            :value="scope.value"
            :label="scope.name"
          />
        </el-select>
        <v-purchase-scope-rules :scope="postData.purchase_scope"/>
      </el-form-item>

      <el-form-item label="唯一性">
        <el-switch
          v-model="postData.is_unique"
          :active-value="1"
          :inactive-value="0"
          active-text="每人限领一张"
          inactive-text="可重复拥有"
        />
        <el-alert
          v-if="postData.is_unique"
          title="开启后，同一位学员最多拥有 1 张此类型的代金券（含开启前已发放的）；修改只影响后续发放，已发出的券不回收"
          type="info"
          show-icon
          :closable="false"
        />
        <el-alert
          v-else
          :title="uniqueAlertTitle"
          :type="uniqueAlertType"
          show-icon
          :closable="false"
        />
      </el-form-item>

      <el-form-item label="叠加使用">
        <el-switch
          v-model="postData.is_stackable"
          :active-value="1"
          :inactive-value="0"
          active-text="可叠加"
          inactive-text="不可叠加"
        />
        <el-alert
          v-if="postData.is_stackable"
          :title="stackableAlertTitle"
          :type="stackableAlertType"
          show-icon
          :closable="false"
        />
        <el-alert
          v-else
          title="关闭时只能单独使用这一张代金券。修改类型不会影响已发放的代金券"
          type="info"
          show-icon
          :closable="false"
        />
      </el-form-item>

      <el-form-item label="适用课卡套餐">
        <v-select-card-charge-type
          v-model="postData.card_charge_type_ids"
          :multiple="true"
          :clearable="true"
          placeholder="请选择课卡套餐（不选则默认全部可用）"
        />
        <el-alert
          title="选择后办理课卡时仅指定套餐可用此代金券，不选则全部套餐可用"
          type="info"
          show-icon
          :closable="false"
        />
      </el-form-item>

      <el-form-item label="使用规则">
        <el-input
          v-model="postData.rule"
          type="textarea"
          :autosize="{ minRows: 3, maxRows: 12 }"
          maxlength="500"
          show-word-limit
          clearable
          placeholder="请输入代金券使用规则"
        />
        <el-alert
          title="办理代金券时会从此处复制使用规则，最大 500 字"
          type="info"
          show-icon
          :closable="false"
        />
      </el-form-item>

      <el-form-item>
        <el-button type="primary" @click="handleSubmit" :loading="isSaving">立即添加</el-button>
        <el-button @click="RouteConfig.voucherTypeList.back">取消</el-button>
      </el-form-item>
    </el-form>
  </section>
</template>

<script>
import RouteConfig from '@/router/config';
import { voucherPurchaseScopes } from '@/enums/voucherPurchaseScopes';
import { store } from '@/api/voucher-type';
import { toCent } from '@/filters';
import { toSecond } from '@/utils/number';

export default {
  name: RouteConfig.voucherTypeAdd.name,
  data: () => ({
    RouteConfig,
    isSaving: false,
    postData: {
      voucher_type_name: '',
      duration_days: 0,
      duration_hours: 0,
      duration_minutes: 0,
      expire_mode: 0,
      fixed_expire_time: '',
      issue_type: 1,
      start_time: '',
      end_time: '',
      amount_in_cents: 0,
      sale_price_in_cents: 0,
      purchase_scope: '',
      is_unique: 1,
      is_stackable: 0,
      card_charge_type_ids: [],
      card_ticket_type_ids: [],
      rule: '',
    },
  }),
  computed: {
    purchaseScopeOptions() {
      return Object.values(voucherPurchaseScopes).filter(scope => scope.value !== 0);
    },
    rules() {
      return {
        voucher_type_name: [{ required: true, message: '请输入代金券类型名称', trigger: 'change' }],
        issue_type: [{ required: true, message: '请选择发放方式', trigger: 'change' }],
        expire_mode: [{ required: true, message: '请选择过期方式', trigger: 'change' }],
        fixed_expire_time: [{ required: true, message: '请选择指定过期时间', trigger: 'change' }],
        duration_days: [{
          validator: (rule, value, callback) => {
            const totalSeconds = this.toSeconds(this.postData.duration_days, this.postData.duration_hours, this.postData.duration_minutes);
            callback(totalSeconds >= 60 && totalSeconds <= 94867200 ? undefined : new Error('请输入 1 分钟 ~ 1098 天范围内的有效时长'));
          },
          trigger: 'change',
        }],
        start_time: [{ required: true, message: '请选择活动开始时间', trigger: 'change' }],
        end_time: [{ required: true, message: '请选择活动结束时间', trigger: 'change' }],
        card_ticket_type_ids: [{ required: true, message: '请选择课次券类型', trigger: 'change' }],
        amount_in_cents: [
          { required: true, message: '请输入代金券面额', trigger: 'change' },
          { validator: (rule, value, callback) => {
            callback(toCent(value) ? undefined : new Error('请输入代金券面额'));
          }, trigger: 'change' },
        ],
        sale_price_in_cents: [
          { validator: (rule, value, callback) => {
            if (this.postData.issue_type !== 5) return callback();
            const salePrice = toCent(value);
            const amount = toCent(this.postData.amount_in_cents);
            if (!salePrice) return callback(new Error('请输入小程序售价'));
            if (amount && salePrice >= amount) return callback(new Error('小程序售价必须低于代金券面额'));
            callback();
          }, trigger: 'change' },
        ],
      };
    },
    // 唯一性/叠加使用的高风险提示：小程序购买 + 可重复拥有/可叠加时用红色强提示
    uniqueAlertType() {
      if (this.postData.is_unique) return 'info';
      return this.postData.issue_type === 5 ? 'error' : 'warning';
    },
    uniqueAlertTitle() {
      return this.postData.issue_type === 5
        ? '危险：该类型在小程序出售且允许重复购买，学员可反复以低价购买高面额代金券，可能造成资损，请确认这是运营主观决定'
        : '关闭后，同一位学员可重复拥有此类型代金券';
    },
    stackableAlertType() {
      if (!this.postData.is_stackable) return 'info';
      return this.postData.issue_type === 5 ? 'error' : 'warning';
    },
    stackableAlertTitle() {
      return this.postData.issue_type === 5
        ? '该代金券在小程序出售且可与其他「可叠加」代金券同时抵扣，可能叠加出超过预期的优惠，请核对适用课卡套餐与面额'
        : '开启后，办卡时可与其他「可叠加」的代金券同时使用；修改类型不会影响已发放的代金券';
    },
    // 活动开始时间选择器限制：不能早于今天
    startTimePickerOptions() {
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      const endMs = this.postData.end_time;
      return {
        disabledDate: (time) => {
          if (time < startOfToday) return true;
          return !!(endMs && time > new Date(endMs));
        },
      };
    },
    // 活动结束时间选择器限制：不能早于开始日期，且不超过开始时间后十年（精确到时分秒）
    endTimePickerOptions() {
      const startMs = this.postData.start_time;
      if (!startMs) {
        return {
          disabledDate: () => false,
        };
      }
      // 取开始日期的 00:00:00，以便当天仍然可选（时分秒由 onEndTimeChange 校验）
      const startDate = new Date(startMs);
      startDate.setHours(0, 0, 0, 0);
      // 精确计算十年后的时间戳
      const maxEndDate = new Date(startMs);
      maxEndDate.setFullYear(maxEndDate.getFullYear() + 10);
      const maxEndMs = maxEndDate.getTime();
      return {
        disabledDate: (time) => {
          return time.getTime() < startDate.getTime() || time.getTime() > maxEndMs;
        },
      };
    },
    // 指定过期时间选择器限制：只能选今天及以后（编辑历史类型时原有过去时间仍正常回显提交）
    fixedExpireTimePickerOptions() {
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      return {
        disabledDate: (time) => time < startOfToday,
      };
    },
  },
  watch: {
    'postData.issue_type'(newVal) {
      // 活动时间：试课自动发放、购买课次券后发放、小程序购买都需要
      if (![1, 4, 5].includes(newVal)) {
        this.postData.start_time = '';
        this.postData.end_time = '';
        this.$nextTick(() => {
          this.$refs.form.clearValidate(['start_time', 'end_time']);
        });
      }

      // 课次券类型：仅购买课次券后发放需要
      if (newVal !== 4) {
        this.postData.card_ticket_type_ids = [];
        this.$nextTick(() => {
          this.$refs.form.clearValidate(['card_ticket_type_ids']);
        });
      }

      // 小程序售价：仅小程序购买需要
      if (newVal !== 5) {
        this.postData.sale_price_in_cents = 0;
        this.$nextTick(() => {
          this.$refs.form.clearValidate(['sale_price_in_cents']);
        });
      }

      // 购买范围：仅小程序购买需要
      if (newVal !== 5) {
        this.postData.purchase_scope = '';
      }
    },
    // 天数达到最大值 1089 时，小时和分钟必须归零
    'postData.duration_days'(val) {
      if (val >= 1089) {
        this.postData.duration_hours = 0;
        this.postData.duration_minutes = 0;
      }
    },
    // 切换过期方式时清除另一方式的校验状态
    'postData.expire_mode'() {
      this.$nextTick(() => {
        this.$refs.form.clearValidate(['duration_days', 'fixed_expire_time']);
      });
    },
  },
  methods: {
    // 结束时间变更时精确校验：不能早于开始时间，不能超过开始时间后十年
    onEndTimeChange(val) {
      if (!val || !this.postData.start_time) return;
      const startMs = this.postData.start_time;
      const maxEndDate = new Date(startMs);
      maxEndDate.setFullYear(maxEndDate.getFullYear() + 10);
      const maxEndMs = maxEndDate.getTime();
      if (val > maxEndMs) {
        this.postData.end_time = maxEndMs;
      } else if (val <= startMs) {
        this.postData.end_time = startMs + 1000;
      }
    },
    toSeconds(days, hours, minutes) {
      return days * 86400 + hours * 3600 + minutes * 60;
    },
    handleSubmit() {
      if (this.isSaving) return;

      this.$refs.form.validate(async (isSuccess) => {
        if (!isSuccess) return;

        const postData = { ...this.postData };

        // 小程序购买 + 可重复拥有：可能被反复低价购买，提交前二次确认
        if (postData.issue_type === 5 && !postData.is_unique) {
          const isConfirmed = await this.$confirm(
            '该类型将在小程序出售且允许重复购买，学员可反复以低价购买高面额代金券，可能造成资损，确认提交？',
            '高风险配置确认',
            { type: 'warning', confirmButtonText: '确认提交', cancelButtonText: '返回修改' },
          ).then(() => true).catch(() => false);
          if (!isConfirmed) return;
        }

        this.isSaving = true;
        const { isError } = await store({
          voucher_type_name: postData.voucher_type_name,
          expire_mode: postData.expire_mode,
          valid_duration_seconds: this.toSeconds(postData.duration_days, postData.duration_hours, postData.duration_minutes),
          fixed_expire_time: postData.fixed_expire_time ? toSecond(postData.fixed_expire_time) : 0,
          issue_type: postData.issue_type,
          start_time: postData.start_time ? toSecond(postData.start_time) : 0,
          end_time: postData.end_time ? toSecond(postData.end_time) : 0,
          amount_in_cents: toCent(postData.amount_in_cents),
          sale_price_in_cents: toCent(postData.sale_price_in_cents),
          purchase_scope: postData.purchase_scope || 0,
          is_unique: postData.is_unique,
          is_stackable: postData.is_stackable,
          card_charge_type_ids: postData.card_charge_type_ids,
          card_ticket_type_ids: postData.card_ticket_type_ids,
          rule: postData.rule,
        });
        this.isSaving = false;
        if (!isError) RouteConfig.voucherTypeList.push();
      });
    },
  },
};
</script>

<style scoped>
.duration-input {
  width: 120px;
  margin-right: 8px;
}

/* 统一各表单项「控件 → 提示条」的间距。
   Element 的 .el-form-item__content 有 40px 固定行高，单选组、开关这类矮控件下方会多出约 9~10px 空行，
   而输入框/下拉/日期框正好 40px 高、没有这段空行；这里用相邻兄弟选择器把多出的部分抵掉，保证视觉间距一致。 */
.el-form-item .el-alert {
  margin-top: 12px;
}

.el-form-item .el-radio-group + .el-alert {
  margin-top: 2px;
}

.el-form-item .el-switch + .el-alert {
  margin-top: 3px;
}

/* 唯一性 / 叠加使用的危险提示：error 类型默认是叉号图标（\e79d），容易被误读成"不能提交"；
   这里只把字形换成警告的感叹号（\e7a3），背景/边框/文字仍保留 danger 红。 */
.el-alert--error /deep/ .el-alert__icon.el-icon-error::before {
  content: "\e7a3";
}
</style>
